import React, { useCallback } from 'react'
import { Platform, View } from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ImageBackground } from 'expo-image'
import { LinearGradient } from 'expo-linear-gradient'
import { Redirect, useLocalSearchParams } from 'expo-router'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { ScreenLoadingFallback } from '@/src/components/ui/ScreenLoadingFallback'
import { useResolvedAuthState } from '@/src/hooks/useResolvedAuthState'
import { colors } from '@/src/theme'
import { useTranslation } from 'react-i18next'

import { OnboardingIntroStep } from '../components/OnboardingIntroStep'
import { OnboardingNameInputStep } from '../components/OnboardingNameInputStep'
import {
  HERO_IMAGE,
  INTRO_GRADIENT_COLORS,
  INTRO_GRADIENT_LOCATIONS,
} from '../constants/onboardingStyles'

const ANDROID_MIN_BOTTOM_INSET = 48

export default function OnboardingScreen() {
  const insets = useSafeAreaInsets()
  const { t } = useTranslation(['common'])
  const { isAuthenticated, isAuthLoading } = useResolvedAuthState()

  const introGradientOpacity = useSharedValue(0)

  const bottomInset =
    Platform.OS === 'android'
      ? Math.max(insets.bottom, ANDROID_MIN_BOTTOM_INSET)
      : insets.bottom

  const { step: stepParam } = useLocalSearchParams<{
    step?: string
  }>()
  const step = stepParam === 'profile' ? 'profile' : 'intro'

  const profileMode = step === 'profile' ? 'visible' : 'hidden'

  const introGradientAnimatedStyle = useAnimatedStyle(() => ({
    opacity: introGradientOpacity.value,
  }))

  const handleHeroDisplay = useCallback(() => {
    introGradientOpacity.set(
      withTiming(1, {
        duration: 400,
        easing: Easing.inOut(Easing.ease),
      }),
    )
  }, [introGradientOpacity])

  if (isAuthenticated) {
    return <Redirect href="/(protected)/(account)/(tabs)" />
  }

  if (isAuthLoading) {
    return <ScreenLoadingFallback />
  }

  return (
    <View className="flex-1">
      <Head>
        <title>{t('common:page_title_onboarding')}</title>
        <meta name="theme-color" content={'#ffffff'} />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content={'#ffffff'}
        />
      </Head>
      <StatusBar style={profileMode === 'hidden' ? 'dark' : 'light'} />
      <ImageBackground
        style={{
          flex: 1,
          justifyContent: 'flex-end',
        }}
        source={HERO_IMAGE}
        transition={{ duration: 400, timing: 'ease-in-out' }}
        contentFit="cover"
        onDisplay={handleHeroDisplay}
      >
        <LinearGradient
          colors={INTRO_GRADIENT_COLORS}
          locations={INTRO_GRADIENT_LOCATIONS}
          style={{
            height: '100%',
            maxHeight: 236,
            justifyContent: 'flex-end',
          }}
        />
        <View
          className="px-6"
          style={{
            backgroundColor: colors.background,
            paddingTop: 16,
            paddingBottom:
              Platform.OS === 'web' ? Math.max(bottomInset, 24) : bottomInset,
            pointerEvents: profileMode === 'visible' ? 'none' : 'auto',
            justifyContent: 'flex-end',
          }}
        >
          <Animated.View style={introGradientAnimatedStyle}>
            <OnboardingIntroStep />
          </Animated.View>
        </View>
        <OnboardingNameInputStep
          topInset={insets.top}
          bottomInset={bottomInset}
          profileMode={profileMode}
        />
      </ImageBackground>
    </View>
  )
}
